import { SearchInstancesManagerService } from '@anecdotes/design-system/data-manipulation';
import { delayedPromise } from '@anecdotes/utils';
import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  Injector,
  Input,
  OnChanges,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import { NEVER, map, switchMap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

export interface ItemContext {
  item: any;
  index: number;
  previousItem?: any;
  nextItem?: any;
  isLast?: boolean;
  isFirst?: boolean;
  isFocusedViaScroll: boolean;
}

export interface ItemMetadata {
  index: number;
  item: any;
}

@Component({
  selector: 'app-base-renderer-v2',
  template: '',
})
// eslint-disable-next-line @angular-eslint/component-class-suffix
export abstract class BaseRendererV2 implements OnInit, OnChanges {
  protected readonly destroyRef = inject(DestroyRef);

  private searchInstancesManagerService: SearchInstancesManagerService;
  private cd: ChangeDetectorRef;


  protected itemsMap: Map<string, ItemMetadata>;
  protected elementRef: ElementRef<HTMLElement>;
  protected itemIds: string[] = [];

  protected focusedId: string;

  @Input() idSelector: (item) => string;
  @Input() items: any[];

  @Output()
  scrolled = new EventEmitter();

  constructor(injector: Injector) {
    this.searchInstancesManagerService = injector.get(SearchInstancesManagerService, null);
    this.cd = injector.get(ChangeDetectorRef);
    this.elementRef = injector.get(ElementRef);
  }

  ngOnInit(): void {
    this.handleSearchPaginator();
    this.scrolled.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.focusedId = undefined;
      this.cd.detectChanges();
    });
  }

  ngOnChanges(): void {
    this.itemsMap = new Map();

    if (this.items) {
      this.items.forEach((item, index) => this.itemsMap.set(this.selectId(item), { index, item }));
      const newIds = Array.from(this.itemsMap.keys());

      if (newIds.join() !== this.itemIds.join()) {
        this.itemIds = newIds;
      }
    }
  }

  scrollIntoAsync(item: any): Promise<void> {
    return this.scrollToIdAsync(this.selectId(item));
  }

  async scrollToIdAsync(id: string, scrollToCenter = false): Promise<void> {
    if (!await this.doesItemExistAsync(id)) {
      return;
    }

    await this.scrollToIdBaseAsync(id, scrollToCenter);
    this.focusedId = id;
    this.cd.detectChanges();
  }

  protected abstract scrollToIdBaseAsync(itemId: string, scrollToCenter: boolean): Promise<void>;

  protected getRenderedItemById(id: string): any {
    return this.itemsMap.get(id)?.item;
  }

  protected selectId(item: any): string {
    if (this.idSelector) {
      return this.idSelector(item);
    }

    return item.id;
  }

  protected createItemTemplateContext(itemId: string): ItemContext {
    const item = this.itemsMap.get(itemId)?.item;

    if (!item) {
      return;
    }

    const itemIndex = this.itemsMap.get(itemId).index;
    const isFirst = itemIndex === 0;
    const isLast = itemIndex >= this.items.length - 1;
    const previousItem = isFirst ? undefined : this.items[itemIndex - 1];
    const nextItem = isLast ? undefined : this.items[itemIndex + 1];

    return {
      item,
      index: itemIndex,
      isFocusedViaScroll: this.isFocusedViaScroll(itemId),
      isFirst,
      isLast,
      previousItem,
      nextItem,
    };
  }

  private isFocusedViaScroll(itemId: any): boolean {
    return itemId === this.focusedId;
  }

  private handleSearchPaginator(): void {
    const currentSearchScopeKey = this.searchInstancesManagerService?.getSearchScopeKey(this.elementRef.nativeElement);

    if (currentSearchScopeKey) {
      this.searchInstancesManagerService
        .getSearchResultsPaginator(currentSearchScopeKey)
        .pipe(
          switchMap((searchPaginator) => {
            if (searchPaginator) {
              return searchPaginator.dataFocusChange.pipe(map(() => searchPaginator));
            }

            return NEVER;
          }),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe((searchPaginator) => this.scrollIntoAsync(searchPaginator.currentOverlapIndexEntry.row));
    }
  }

  /**
   * Asynchronously checks if item id exists. It will take 3 attempts to check if item exists, and:
   * - if exists, returns true
   * - if does not exist, returns false
   * @param itemId
   * @returns
   */
  private async doesItemExistAsync(itemId: string): Promise<boolean> {
    for (let attempt = 0; attempt < 3; attempt++) {
      if (attempt > 0) {
        await delayedPromise(500);
      }

      if (this.itemsMap.has(itemId)) {
        return true;
      }
    }

    return false;
  }
}
